import { useState, useCallback, useEffect } from 'react';
import { Session } from '@supabase/supabase-js';
import { isPasswordValid } from '../../../../../utils/passwordValidation';
import { validateEmail, validateUsername } from '../../../../../utils/accountValidation';
import { categorizeAuthError, getRecoverySuggestions, trackAuthError } from '../../../../../utils/authErrorHandler';
import { createCheckoutSession } from '../../../../../services/stripe';
import { getPendingCheckout, clearPendingCheckout } from '../../../../../utils/pendingCheckout';
import { fetchActiveTermsVersion } from '../../../../../services/terms';

const CAPTCHA_REQUIRED_MESSAGE = 'Please complete the CAPTCHA challenge';
const REQUIRE_LOGIN_CAPTCHA = (process.env.REACT_APP_REQUIRE_LOGIN_CAPTCHA ?? 'true') !== 'false';

export type AuthMode = 'login' | 'signup';

interface AuthActions {
  signIn: (email: string, password: string, captchaToken?: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, username: string, captchaToken?: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signInWithApple: () => Promise<void>;
  resendVerificationEmail: () => Promise<{ error: Error | null }>;
  checkUsernameAvailability: (username: string) => Promise<boolean>;
  checkEmailConfirmationStatus: (email: string) => Promise<{ exists: boolean; confirmed: boolean; available: boolean }>;
  acceptTerms: (termsVersionId: string) => Promise<{ error: Error | null }>;
}

interface UseAuthFormProps {
  session: Session | null;
  authActions: AuthActions;
  isOpen: boolean;
  theme: 'light' | 'dark';
  skipCaptcha?: boolean;
}

interface UseAuthFormReturn {
  // Form state
  mode: AuthMode;
  email: string;
  password: string;
  confirmPassword: string;
  username: string;
  setMode: (mode: AuthMode) => void;
  setEmail: (email: string) => void;
  setPassword: (password: string) => void;
  setConfirmPassword: (confirmPassword: string) => void;
  setUsername: (username: string) => void;

  // Password visibility
  showPassword: boolean;
  showConfirmPassword: boolean;
  setShowPassword: (show: boolean) => void;
  setShowConfirmPassword: (show: boolean) => void;

  // CAPTCHA state
  captchaToken: string | null;
  captchaResetCounter: number;
  setCaptchaToken: (token: string | null) => void;
  shouldRenderCaptcha: boolean;

  // Terms state
  termsAccepted: boolean;
  termsError: string;
  showTermsModal: boolean;
  activeTermsVersionId: string | null;
  setTermsAccepted: (accepted: boolean) => void;
  setTermsError: (error: string) => void;
  setShowTermsModal: (show: boolean) => void;

  // Messages
  error: string;
  successMessage: string;
  errorType: 'error' | 'warning';
  recoverySuggestions: string[];
  showLoginSuggestion: boolean;
  showResendVerification: boolean;
  setError: (error: string) => void;
  setSuccessMessage: (message: string) => void;

  // Verification
  resendingVerification: boolean;
  showEmailVerificationModal: boolean;
  verificationEmail: string;
  setShowEmailVerificationModal: (show: boolean) => void;

  // Checkout
  hasPendingCheckout: boolean;

  // OAuth labels
  googleButtonLabel: string;
  appleButtonLabel: string;

  // Handlers
  handleEmailAuth: (
    e: React.FormEvent,
    emailAvailable: boolean | null,
    setEmailAvailable: (available: boolean | null) => void,
    usernameAvailable: boolean | null,
    setUsernameAvailable: (available: boolean | null) => void
  ) => Promise<void>;
  handleLogout: () => Promise<void>;
  handleGoogleLogin: () => Promise<void>;
  handleAppleLogin: () => Promise<void>;
  handleResendVerification: () => Promise<void>;
  handleModeSwitch: (resetEmailValidation: () => void) => void;
  resetCaptcha: () => void;
  handleTryLogin: () => void;
}

export const useAuthForm = ({
  session,
  authActions,
  isOpen,
  theme,
  skipCaptcha = false,
}: UseAuthFormProps): UseAuthFormReturn => {
  // Form state
  const [mode, setMode] = useState<AuthMode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [username, setUsername] = useState('');

  // Password visibility
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // CAPTCHA state
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaResetCounter, setCaptchaResetCounter] = useState(0);

  // Terms state
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState('');
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [activeTermsVersionId, setActiveTermsVersionId] = useState<string | null>(null);

  // Messages
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorType, setErrorType] = useState<'error' | 'warning'>('error');
  const [recoverySuggestions, setRecoverySuggestions] = useState<string[]>([]);
  const [showLoginSuggestion, setShowLoginSuggestion] = useState(false);
  const [showResendVerification, setShowResendVerification] = useState(false);

  // Verification
  const [resendingVerification, setResendingVerification] = useState(false);
  const [showEmailVerificationModal, setShowEmailVerificationModal] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');

  const shouldRenderCaptcha = !skipCaptcha && (mode === 'signup' || REQUIRE_LOGIN_CAPTCHA);
  const hasPendingCheckout = Boolean(getPendingCheckout());

  // OAuth button configuration
  const googleButtonLabel = mode === 'signup' ? 'Sign up with Google' : 'Sign in with Google';
  const appleButtonLabel = mode === 'signup' ? 'Sign up with Apple' : 'Sign in with Apple';

  // Fetch active terms version when modal opens (for signup)
  useEffect(() => {
    const fetchActiveTerms = async () => {
      if (isOpen && mode === 'signup') {
        try {
          const terms = await fetchActiveTermsVersion();
          setActiveTermsVersionId(terms?.id ?? null);
        } catch (err) {
          console.error('Error fetching active terms:', err);
          setActiveTermsVersionId(null);
        }
      }
    };

    fetchActiveTerms();
  }, [isOpen, mode]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setShowEmailVerificationModal(false);
      setVerificationEmail('');
      setCaptchaToken(null);
      setCaptchaResetCounter(prev => prev + 1);
      setShowPassword(false);
      setShowConfirmPassword(false);
    }
  }, [isOpen]);

  // Process pending checkout after auth
  const processPendingCheckout = useCallback(async () => {
    const pendingPriceId = getPendingCheckout();

    if (pendingPriceId && session?.access_token) {
      try {
        const { url } = await createCheckoutSession(pendingPriceId, session.access_token);
        clearPendingCheckout();
        window.location.href = url;
      } catch (err) {
        console.error('Failed to initiate checkout after auth:', err);
        setError('Authentication successful, but checkout failed. Please try again.');
        clearPendingCheckout();
      }
    }
  }, [session]);

  const resetCaptcha = useCallback(() => {
    setCaptchaToken(null);
    setCaptchaResetCounter(prev => prev + 1);
  }, []);

  const handleEmailAuth = useCallback(async (
    e: React.FormEvent,
    emailAvailable: boolean | null,
    setEmailAvailable: (available: boolean | null) => void,
    usernameAvailable: boolean | null,
    setUsernameAvailable: (available: boolean | null) => void
  ) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    try {
      if (mode === 'login') {
        if (!skipCaptcha && REQUIRE_LOGIN_CAPTCHA && !captchaToken) {
          setError(CAPTCHA_REQUIRED_MESSAGE);
          return;
        }
        const { error } = await authActions.signIn(email, password, captchaToken || undefined);
        if (error) throw error;
        setEmail('');
        setPassword('');
        setShowPassword(false);
        resetCaptcha();

        // Check for pending checkout after successful sign-in
        setTimeout(() => processPendingCheckout(), 100);
      } else {
        // SIGNUP MODE
        const trimmedEmail = email.trim();
        const trimmedUsername = username.trim();

        // Validate email format
        const emailValidation = validateEmail(trimmedEmail);
        if (!emailValidation.isValid) {
          setError(emailValidation.error || 'Invalid email address');
          return;
        }

        // Check email confirmation status
        const emailStatus = await authActions.checkEmailConfirmationStatus(trimmedEmail);
        setEmailAvailable(emailStatus.available);

        if (emailStatus.exists && emailStatus.confirmed) {
          setError('Email already registered. Please login instead.');
          setErrorType('warning');
          setShowLoginSuggestion(true);
          setRecoverySuggestions([
            'Click "Try Login Instead" to sign in to your existing account',
            'Use "Forgot Password" if you don\'t remember your password'
          ]);
          return;
        } else if (emailStatus.exists && !emailStatus.confirmed) {
          setError('An account with this email exists but is unconfirmed. Check your email for verification link or resend it below.');
          setErrorType('warning');
          setShowResendVerification(true);
          setVerificationEmail(trimmedEmail);
          setRecoverySuggestions([
            'Check your email inbox for the verification link',
            'Check your spam/junk folder',
            'Click "Resend Verification Email" to get a new link'
          ]);
          return;
        }

        if (!trimmedUsername) {
          setError('Username is required');
          return;
        }

        if (!isPasswordValid(password)) {
          setError('Password does not meet requirements');
          return;
        }

        if (password !== confirmPassword) {
          setError('Passwords do not match');
          return;
        }

        const usernameValidation = validateUsername(trimmedUsername);
        if (!usernameValidation.isValid) {
          setError(usernameValidation.error || 'Invalid username');
          return;
        }

        const isAvailable = await authActions.checkUsernameAvailability(trimmedUsername);
        setUsernameAvailable(isAvailable);
        if (!isAvailable) {
          setError('Username already taken');
          return;
        }

        if (!termsAccepted) {
          setTermsError('You must accept the Terms & Conditions to sign up');
          return;
        }

        if (!skipCaptcha && !captchaToken) {
          setError(CAPTCHA_REQUIRED_MESSAGE);
          return;
        }

        const { error } = await authActions.signUp(email, password, trimmedUsername, captchaToken ?? undefined);
        if (error) throw error;

        setSuccessMessage('Check your confirmation email!');
        setVerificationEmail(email);
        setShowEmailVerificationModal(true);

        // Defer terms acceptance until after email confirmation (SIGNED_IN event)
        if (activeTermsVersionId) {
          localStorage.setItem('wsle:pendingTermsAcceptance', activeTermsVersionId);
        }

        resetCaptcha();

        // Clear form
        setEmail('');
        setPassword('');
        setConfirmPassword('');
        setUsername('');
        setUsernameAvailable(null);
        setTermsAccepted(false);
        setTermsError('');
        setShowPassword(false);
        setShowConfirmPassword(false);

        setTimeout(() => processPendingCheckout(), 100);
      }
    } catch (err: any) {
      const categorizedError = categorizeAuthError(err);
      const suggestions = getRecoverySuggestions(categorizedError);

      trackAuthError(categorizedError, { mode, email });

      setError(categorizedError.userMessage);
      setRecoverySuggestions(suggestions);

      const isWarning = categorizedError.code === 'CAPTCHA_FAILED' ||
                       categorizedError.code === 'CAPTCHA_TIMEOUT' ||
                       categorizedError.code === 'EMAIL_EXISTS';
      setErrorType(isWarning ? 'warning' : 'error');

      if (categorizedError.actionRequired === 'LOGIN') {
        setShowLoginSuggestion(true);
      } else if (categorizedError.code === 'CAPTCHA_FAILED' ||
                 categorizedError.code === 'CAPTCHA_TIMEOUT' ||
                 categorizedError.code === 'CAPTCHA_INVALID') {
        resetCaptcha();
      }
    }
  }, [mode, email, password, confirmPassword, username, captchaToken, termsAccepted, activeTermsVersionId, authActions, processPendingCheckout, resetCaptcha, skipCaptcha]);

  const handleLogout = useCallback(async () => {
    try {
      setError('');
      setSuccessMessage('');
      await authActions.signOut();
    } catch (err: any) {
      console.error('Logout error:', err);
      setError(err.message || 'Logout failed');
    }
  }, [authActions]);

  const handleGoogleLogin = useCallback(async () => {
    try {
      setError('');
      await authActions.signInWithGoogle();
    } catch (err: any) {
      setError(err.message || 'Google login failed');
      clearPendingCheckout();
    }
  }, [authActions]);

  const handleAppleLogin = useCallback(async () => {
    try {
      setError('');
      await authActions.signInWithApple();
    } catch (err: any) {
      setError(err.message || 'Apple login failed');
    }
  }, [authActions]);

  const handleResendVerification = useCallback(async () => {
    setError('');
    setSuccessMessage('');

    try {
      setResendingVerification(true);
      const { error } = await authActions.resendVerificationEmail();

      if (error) {
        setError(error.message || 'Failed to resend verification email');
      } else {
        setSuccessMessage('Verification email sent! Check your inbox.');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to resend verification email');
    } finally {
      setResendingVerification(false);
    }
  }, [authActions]);

  const handleModeSwitch = useCallback((resetEmailValidation: () => void) => {
    setMode(mode === 'login' ? 'signup' : 'login');
    setError('');
    setSuccessMessage('');
    resetEmailValidation();
    resetCaptcha();
    setShowPassword(false);
    setShowConfirmPassword(false);
  }, [mode, resetCaptcha]);

  const handleTryLogin = useCallback(() => {
    setMode('login');
    setError('');
    setShowLoginSuggestion(false);
    setRecoverySuggestions([]);
  }, []);

  return {
    mode,
    email,
    password,
    confirmPassword,
    username,
    setMode,
    setEmail,
    setPassword,
    setConfirmPassword,
    setUsername,
    showPassword,
    showConfirmPassword,
    setShowPassword,
    setShowConfirmPassword,
    captchaToken,
    captchaResetCounter,
    setCaptchaToken,
    shouldRenderCaptcha,
    termsAccepted,
    termsError,
    showTermsModal,
    activeTermsVersionId,
    setTermsAccepted,
    setTermsError,
    setShowTermsModal,
    error,
    successMessage,
    errorType,
    recoverySuggestions,
    showLoginSuggestion,
    showResendVerification,
    setError,
    setSuccessMessage,
    resendingVerification,
    showEmailVerificationModal,
    verificationEmail,
    setShowEmailVerificationModal,
    hasPendingCheckout,
    googleButtonLabel,
    appleButtonLabel,
    handleEmailAuth,
    handleLogout,
    handleGoogleLogin,
    handleAppleLogin,
    handleResendVerification,
    handleModeSwitch,
    resetCaptcha,
    handleTryLogin,
  };
};
